import { type ReactNode } from "react";

import { tenantConfig } from "@tenant-config";
import Image1 from "@vega-assets/images/login-banner-image-1.svg?react";
import Image2 from "@vega-assets/images/login-banner-image-2.svg?react";
import Image3 from "@vega-assets/images/login-banner-image-3.svg?react";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";

export type BannerItem = {
  color: string;
  description: string;
  icon: ReactNode;
  title: string;
};

export const DEFAULT_LOGIN_BANNER_COLORS: [string, string, string] = [
  "#54585a",
  "#ec9a5f",
  "#ccc2b9",
];

const getDefaultItems = (companyName: string): BannerItem[] => [
  {
    color: DEFAULT_LOGIN_BANNER_COLORS[0],
    description: m.lp_login_browseDescription({ companyName }),
    icon: <Image1 />,
    title: m.lp_login_browseTitle(),
  },
  {
    color: DEFAULT_LOGIN_BANNER_COLORS[1],
    description: m.lp_login_operationsDescription(),
    icon: <Image2 />,
    title: m.lp_login_operationsTitle(),
  },
  {
    color: DEFAULT_LOGIN_BANNER_COLORS[2],
    description: m.lp_login_subscribeDescription(),
    icon: <Image3 />,
    title: m.lp_login_subscribeTitle(),
  },
];

type Props = {
  items?: BannerItem[];
};

export function Banner({ items }: Props) {
  const {
    global: { companyName },
  } = usePublicConfig();
  const { assets } = tenantConfig.branding;
  const resolvedItems = items ?? getDefaultItems(companyName);

  if (assets?.auth?.backgroundImage) {
    return (
      <div className="h-full rounded-lg flex flex-col xl:flex-row overflow-hidden relative">
        <img
          alt="Banner"
          className="flex-1 object-cover"
          src={assets.auth.backgroundImage}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 p-6 xl:p-8 flex flex-col gap-4 ">
          <h1 className="heading-3 text-text/auth/primary">
            {m.auth_bannerTitle()}
          </h1>
          <p className="body-base-regular text-text/auth/primary">
            {m.auth_bannerDescription()}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full rounded-lg border border-border/primary flex flex-col xl:flex-row overflow-hidden">
      {resolvedItems.map((item) => (
        <div
          className="flex-1 px-6 py-13 flex flex-col justify-end gap-8"
          key={item.title}
          style={{ backgroundColor: item.color }}
        >
          <div className="gap-4 xl:gap-8 flex flex-col">
            <div className="h-8 w-8 xl:h-20 xl:w-20 relative flex items-center justify-center">
              {item.icon}
            </div>
            <div className="flex flex-col gap-2">
              <div className="text-white body-semi-large-medium xl:body-extra-large-medium">
                {item.title}
              </div>
              <div className="text-white body-base-regular">
                {item.description}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
